// Модель дашборда: значения по умолчанию и нормализация сохранённых определений.

import type {
  DashboardDefinition,
  DashboardScope,
  StoredDashboardDefinition,
  StoredWidgetDefinition,
  WidgetAppearance,
  WidgetDefinition,
  WidgetTimeSettings,
} from '../types';
import { normalizeDashboardTheme } from '../theme';

import { initialPreferences } from './preferences';

export const DEFAULT_APPEARANCE: WidgetAppearance = {
  numberFormat: 'auto',
  showUnit: true,
  labelMode: 'auto',
  showTooltip: true,
  showGrid: true,
  animation: 'auto',
  palette: 'dashboard',
  classification: 'quantile',
  classCount: 5,
  manualBreaks: [],
  reversePalette: false,
  showLegend: true,
  legendPosition: 'auto',
  showLabels: false,
  nullColor: '#e2e8f0',
  decimals: 1,
  condition: { operator: 'none', value: null, color: '#dc2626' },
  orientation: 'auto',
  sortOrder: 'auto',
  topN: 0,
  axisScale: 'auto',
  includeZero: 'auto',
  xAxisMin: null,
  xAxisMax: null,
  axisMin: null,
  axisMax: null,
  smoothLines: false,
  lineWidth: 2,
  lineStyle: 'solid',
  lineCurve: 'linear',
  connectNulls: false,
  areaOpacity: 0.22,
  areaFillMode: 'gradient',
  seriesStackMode: 'none',
  showPoints: 'auto',
  dataZoom: 'auto',
  usePatterns: false,
  barLayout: 'grouped',
  barWidth: null,
  barGap: 28,
  showTotalLabels: false,
  barLabelPosition: 'auto',
  otherThreshold: 0,
  pieVariant: 'pie',
  pieInnerRadius: 0,
  pieOuterRadius: 68,
  pieLabelMode: 'auto',
  pieMinSlicePercent: 0,
  pieStartAngle: 90,
  pieRoseMode: 'none',
  heatmapTranspose: false,
  heatmapScaleMode: 'auto',
  heatmapFixedMin: null,
  heatmapFixedMax: null,
  heatmapMissingColor: '#e2e8f0',
  heatmapCellGap: 1,
  heatmapCellBorder: true,
  heatmapAggregation: 'latest',
  pyramidSexDimension: 'grup_2',
  pyramidAgeDimension: 'vozr',
  pyramidMaleValue: 'male',
  pyramidFemaleValue: 'female',
  pyramidMode: 'absolute',
  pyramidMaleColor: '#2563eb',
  pyramidFemaleColor: '#f472b6',
  pyramidAgeOrder: 'asc',
  kpiIconMode: 'none',
  kpiIcon: 'activity',
  kpiAggregation: 'latest',
  kpiComparison: 'previous',
  kpiTarget: null,
  kpiSparkline: true,
  kpiSparklineStyle: 'line',
  tableColumns: 'auto',
  tableDensity: 'comfortable',
  tablePageSize: 25,
  tableShowQuality: true,
  tableCellVisual: 'none',
  mapScaleMode: 'global',
  mapFixedMin: null,
  mapFixedMax: null,
  mapLegendMode: 'collapsed',
  mapBasemap: 'auto',
  mapFillOpacity: 0.82,
  mapOutlineColor: '#ffffff',
  mapOutlineWidth: 0.75,
  mapBasemapOpacity: 0.82,
  mapSymbolMinRadius: 5,
  mapSymbolMaxRadius: 28,
  mapSymbolScale: 'sqrt',
  mapSymbolStrokeColor: '#ffffff',
  mapSymbolStrokeWidth: 1.4,
  mapSymbolOpacity: 0.82,
  scatterBubbleMode: 'value',
  networkMode: 'proximity',
  flowMode: 'net',
  flowMapMode: 'net',
  miniMapGroup: 'federal_district',
};

export const DEFAULT_TIME: WidgetTimeSettings = {
  activePeriod: null,
  showControl: true,
};

export const DEFAULT_DONUT_INNER_RADIUS = 48;

export function blankDashboard(title: string): DashboardDefinition {
  return {
    schemaVersion: 5,
    title,
    theme: {
      primaryColor: initialPreferences.lastPrimaryColor,
    },
    globalFilters: { year: null, territoryId: null },
    widgets: [],
  };
}

export function clone<T>(value: T): T {
  return structuredClone(value);
}

export function createClientId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
  const bytes = new Uint8Array(16);
  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function choice<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

export function finiteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function optionalRawNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function clamp(value: unknown, min: number, max: number, fallback: number): number {
  return Math.max(min, Math.min(max, finiteNumber(value, fallback)));
}

export function normalizeAppearance(raw?: Partial<WidgetAppearance>): WidgetAppearance {
  const basemapValues = new Set(['auto', 'blankLight', 'blankDark', 'osm', 'satellite']);
  const rawBasemap = typeof raw?.mapBasemap === 'string' ? raw.mapBasemap : '';
  const rawPieVariant = typeof raw?.pieVariant === 'string' ? raw.pieVariant : '';
  const inferredPieVariant: WidgetAppearance['pieVariant'] =
    rawPieVariant === 'donut' || (!rawPieVariant && Number(raw?.pieInnerRadius) > 0) ? 'donut' : 'pie';
  const normalized = {
    ...DEFAULT_APPEARANCE,
    ...(raw ?? {}),
    manualBreaks: Array.isArray(raw?.manualBreaks) ? raw.manualBreaks.filter(Number.isFinite) : [],
    numberFormat: choice(raw?.numberFormat, ['auto', 'plain', 'compact'], DEFAULT_APPEARANCE.numberFormat),
    labelMode: choice(raw?.labelMode, ['auto', 'none', 'value', 'percent', 'value_percent', 'name_value'], DEFAULT_APPEARANCE.labelMode),
    animation: choice(raw?.animation, ['auto', 'on', 'off'], DEFAULT_APPEARANCE.animation),
    xAxisMin: optionalRawNumber(raw?.xAxisMin),
    xAxisMax: optionalRawNumber(raw?.xAxisMax),
    axisMin: optionalRawNumber(raw?.axisMin),
    axisMax: optionalRawNumber(raw?.axisMax),
    lineWidth: clamp(raw?.lineWidth, 1, 8, DEFAULT_APPEARANCE.lineWidth),
    lineStyle: choice(raw?.lineStyle, ['solid', 'dashed', 'dotted'], DEFAULT_APPEARANCE.lineStyle),
    lineCurve: choice(raw?.lineCurve, ['linear', 'smooth', 'step-start', 'step-middle', 'step-end'], raw?.smoothLines ? 'smooth' : DEFAULT_APPEARANCE.lineCurve),
    areaOpacity: clamp(raw?.areaOpacity, 0, 1, DEFAULT_APPEARANCE.areaOpacity),
    areaFillMode: choice(raw?.areaFillMode, ['solid', 'gradient'], DEFAULT_APPEARANCE.areaFillMode),
    seriesStackMode: choice(raw?.seriesStackMode, ['none', 'stacked', 'percent'], DEFAULT_APPEARANCE.seriesStackMode),
    barLayout: choice(raw?.barLayout, ['grouped', 'stacked', 'percent'], DEFAULT_APPEARANCE.barLayout),
    barWidth: optionalRawNumber(raw?.barWidth),
    barGap: clamp(raw?.barGap, 0, 80, DEFAULT_APPEARANCE.barGap),
    barLabelPosition: choice(raw?.barLabelPosition, ['auto', 'inside', 'outside', 'left', 'right', 'top', 'bottom'], DEFAULT_APPEARANCE.barLabelPosition),
    otherThreshold: clamp(raw?.otherThreshold, 0, 20, DEFAULT_APPEARANCE.otherThreshold),
    pieVariant: choice(raw?.pieVariant, ['pie', 'donut'], inferredPieVariant),
    pieInnerRadius: inferredPieVariant === 'pie'
      ? 0
      : clamp(raw?.pieInnerRadius, 1, 80, DEFAULT_DONUT_INNER_RADIUS),
    pieOuterRadius: clamp(raw?.pieOuterRadius, 30, 90, DEFAULT_APPEARANCE.pieOuterRadius),
    pieLabelMode: choice(raw?.pieLabelMode, ['auto', 'none', 'outside', 'inside', 'legend'], DEFAULT_APPEARANCE.pieLabelMode),
    pieMinSlicePercent: clamp(raw?.pieMinSlicePercent, 0, 20, DEFAULT_APPEARANCE.pieMinSlicePercent),
    pieStartAngle: clamp(raw?.pieStartAngle, 0, 360, DEFAULT_APPEARANCE.pieStartAngle),
    pieRoseMode: choice(raw?.pieRoseMode, ['none', 'radius', 'area'], DEFAULT_APPEARANCE.pieRoseMode),
    heatmapScaleMode: choice(raw?.heatmapScaleMode, ['auto', 'global', 'fixed'], DEFAULT_APPEARANCE.heatmapScaleMode),
    heatmapFixedMin: optionalRawNumber(raw?.heatmapFixedMin),
    heatmapFixedMax: optionalRawNumber(raw?.heatmapFixedMax),
    heatmapCellGap: clamp(raw?.heatmapCellGap, 0, 8, DEFAULT_APPEARANCE.heatmapCellGap),
    heatmapAggregation: choice(raw?.heatmapAggregation, ['sum', 'mean', 'min', 'max', 'latest'], DEFAULT_APPEARANCE.heatmapAggregation),
    pyramidMode: choice(raw?.pyramidMode, ['absolute', 'percent'], DEFAULT_APPEARANCE.pyramidMode),
    pyramidAgeOrder: choice(raw?.pyramidAgeOrder, ['asc', 'desc'], DEFAULT_APPEARANCE.pyramidAgeOrder),
    kpiIconMode: choice(raw?.kpiIconMode, ['auto', 'custom', 'none'], DEFAULT_APPEARANCE.kpiIconMode),
    kpiIcon: typeof raw?.kpiIcon === 'string' && raw.kpiIcon.trim()
      ? raw.kpiIcon.trim().replaceAll(/[^a-z0-9-]/gi, '').slice(0, 40) || DEFAULT_APPEARANCE.kpiIcon
      : DEFAULT_APPEARANCE.kpiIcon,
    kpiAggregation: choice(raw?.kpiAggregation, ['latest', 'sum', 'mean', 'min', 'max'], DEFAULT_APPEARANCE.kpiAggregation),
    kpiComparison: choice(raw?.kpiComparison, ['none', 'previous', 'first', 'target'], DEFAULT_APPEARANCE.kpiComparison),
    kpiTarget: optionalRawNumber(raw?.kpiTarget),
    kpiSparklineStyle: choice(raw?.kpiSparklineStyle, ['line', 'area', 'bar'], DEFAULT_APPEARANCE.kpiSparklineStyle),
    tableColumns: choice(raw?.tableColumns, ['auto', 'compact', 'full'], DEFAULT_APPEARANCE.tableColumns),
    tableDensity: choice(raw?.tableDensity, ['comfortable', 'compact', 'dense'], DEFAULT_APPEARANCE.tableDensity),
    tablePageSize: Math.round(clamp(raw?.tablePageSize, 5, 100, DEFAULT_APPEARANCE.tablePageSize)),
    tableCellVisual: choice(raw?.tableCellVisual, ['none', 'bars', 'heatmap', 'font_scale'], DEFAULT_APPEARANCE.tableCellVisual),
    mapLegendMode: choice(raw?.mapLegendMode, ['collapsed', 'open', 'hidden'], DEFAULT_APPEARANCE.mapLegendMode),
    mapBasemap: basemapValues.has(rawBasemap) ? rawBasemap as WidgetAppearance['mapBasemap'] : DEFAULT_APPEARANCE.mapBasemap,
    mapFillOpacity: clamp(raw?.mapFillOpacity, 0.1, 1, DEFAULT_APPEARANCE.mapFillOpacity),
    mapOutlineWidth: clamp(raw?.mapOutlineWidth, 0, 4, DEFAULT_APPEARANCE.mapOutlineWidth),
    mapBasemapOpacity: clamp(raw?.mapBasemapOpacity, 0, 1, DEFAULT_APPEARANCE.mapBasemapOpacity),
    mapSymbolMinRadius: clamp(raw?.mapSymbolMinRadius, 1, 30, DEFAULT_APPEARANCE.mapSymbolMinRadius),
    mapSymbolMaxRadius: clamp(raw?.mapSymbolMaxRadius, 2, 80, DEFAULT_APPEARANCE.mapSymbolMaxRadius),
    mapSymbolScale: choice(raw?.mapSymbolScale, ['linear', 'sqrt'], DEFAULT_APPEARANCE.mapSymbolScale),
    mapSymbolStrokeWidth: clamp(raw?.mapSymbolStrokeWidth, 0, 6, DEFAULT_APPEARANCE.mapSymbolStrokeWidth),
    mapSymbolOpacity: clamp(raw?.mapSymbolOpacity, 0.1, 1, DEFAULT_APPEARANCE.mapSymbolOpacity),
    scatterBubbleMode: choice(raw?.scatterBubbleMode, ['none', 'value', 'comparison'], DEFAULT_APPEARANCE.scatterBubbleMode),
    networkMode: choice(raw?.networkMode, ['proximity', 'correlation'], DEFAULT_APPEARANCE.networkMode),
    flowMode: choice(raw?.flowMode, ['hierarchy', 'net'], DEFAULT_APPEARANCE.flowMode),
    flowMapMode: choice(raw?.flowMapMode, ['net'], DEFAULT_APPEARANCE.flowMapMode),
    miniMapGroup: choice(raw?.miniMapGroup, ['federal_district', 'macroregion'], DEFAULT_APPEARANCE.miniMapGroup),
    condition: { ...DEFAULT_APPEARANCE.condition, ...(raw?.condition ?? {}) },
  };
  if (normalized.axisMax != null && normalized.axisMin != null && normalized.axisMax <= normalized.axisMin) {
    normalized.axisMin = null;
    normalized.axisMax = null;
  }
  if (normalized.xAxisMax != null && normalized.xAxisMin != null && normalized.xAxisMax <= normalized.xAxisMin) {
    normalized.xAxisMin = null;
    normalized.xAxisMax = null;
  }
  if (normalized.pieInnerRadius >= normalized.pieOuterRadius) {
    normalized.pieInnerRadius = Math.max(0, normalized.pieOuterRadius - 18);
  }
  if (normalized.mapSymbolMaxRadius < normalized.mapSymbolMinRadius) {
    normalized.mapSymbolMaxRadius = normalized.mapSymbolMinRadius;
  }
  return normalized;
}

export function normalizeWidget(raw: StoredWidgetDefinition, schemaVersion = 3): WidgetDefinition {
  const appearance = normalizeAppearance(raw.appearance);
  if (schemaVersion < 3 && appearance.palette === 'blue') {
    appearance.palette = 'dashboard';
  }
  return {
    ...raw,
    appearance,
    time: { ...DEFAULT_TIME, ...(raw.time ?? {}) },
    query: {
      indicatorId: raw.query.indicatorId,
      compareIndicatorIds: [...(raw.query.compareIndicatorIds ?? [])],
      territoryIds: [...(raw.query.territoryIds ?? [])],
      years: [...(raw.query.years ?? [])],
      months: [...(raw.query.months ?? [])],
      dimensions: raw.query.dimensions ?? {},
    },
    layout: {
      x: raw.layout?.x ?? 0,
      y: raw.layout?.y ?? 0,
      w: raw.layout?.w ?? 6,
      h: raw.layout?.h ?? 4,
    },
  };
}

function normalizeScope(raw: unknown): DashboardScope | null {
  const scope = raw as Partial<DashboardScope> | null | undefined;
  if (!scope || (scope.level !== 'federal_district' && scope.level !== 'region')) return null;
  return typeof scope.territoryId === 'string' && scope.territoryId ? { level: scope.level, territoryId: scope.territoryId } : null;
}

export function normalizeDashboard(raw: StoredDashboardDefinition, fallbackTitle: string): DashboardDefinition {
  const filters = raw.globalFilters as Partial<DashboardDefinition['globalFilters']> | undefined;
  const schemaVersion = Math.max(5, raw.schemaVersion ?? 1);
  return {
    schemaVersion,
    // Имя из колонки name — источник истины; raw.title остаётся только
    // для несохранённого дашборда, у которого имени в базе ещё нет.
    title: fallbackTitle || raw.title || '',
    theme: normalizeDashboardTheme(raw.theme),
    globalFilters: {
      // Фильтры года и территории по загруженным строкам больше не применяются
      // (их панель заменил территориальный фильтр) — при сохранении сбрасываются.
      year: null,
      territoryId: null,
      scope: normalizeScope(filters?.scope),
    },
    widgets: (raw.widgets ?? []).map((widget) => normalizeWidget(widget, raw.schemaVersion ?? 1)),
  };
}
